import api from '../config/api';

export interface ShippingAddress {
  business_name: string;
  contact_name?: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface TaxItem {
  sku: string;
  quantity: number;
  amount: number;
  tax_code?: string;
  description?: string;
}

export interface TaxBreakdown {
  jurisdiction: string;
  name: string;
  rate: number;
  tax: number;
  taxable: number;
}

export interface TaxCalculation {
  total_tax: number;
  tax_rate: number;
  taxable_amount: number;
  total_amount: number;
  tax_breakdown: TaxBreakdown[];
  error?: string;
}

export interface TaxCalculationRequest {
  qbo_customer_id: string;
  shipping_address: ShippingAddress;
  items: TaxItem[];
  commit_transaction?: boolean;
}

export interface TaxCalculationResponse {
  success: boolean;
  customer_validated: boolean;
  tax_calculation: TaxCalculation;
  transaction_id?: string;
  error_message?: string;
}

export interface AddressValidationResponse {
  valid: boolean;
  validation_status: 'validated' | 'corrected' | 'po_box' | 'undeliverable' | 'error' | 'not_configured' | 'timeout';
  original_address: ShippingAddress;
  suggested_address: ShippingAddress | null;
  is_po_box?: boolean;
  resolution_quality?: string;
  messages: string[];
}

class TaxService {
  /**
   * Validate QBO Customer ID and calculate tax
   * @param request Tax calculation request
   * @returns Tax calculation response
   */
  async validateAndCalculateTax(request: TaxCalculationRequest): Promise<TaxCalculationResponse> {
    try {
      const response = await api.post<TaxCalculationResponse>('/tax/validate-and-calculate', request);
      return response.data;
    } catch (error: any) {
      console.error('Tax calculation error:', error);
      
      // Return error response
      return {
        success: false,
        customer_validated: false,
        tax_calculation: {
          total_tax: 0,
          tax_rate: 0,
          taxable_amount: 0,
          total_amount: 0,
          tax_breakdown: [],
          error: error.response?.data?.detail || 'Failed to calculate tax'
        },
        error_message: error.response?.data?.detail || 'Failed to calculate tax'
      };
    }
  }

  /**
   * Ping Avalara service to check connectivity
   * @returns Ping response
   */
  async pingAvalara(): Promise<any> {
    try {
      const response = await api.get('/tax/ping');
      return response.data;
    } catch (error) {
      console.error('Avalara ping error:', error);
      throw error;
    }
  }

  /**
   * Validate an address using Avalara
   * @param address Address to validate
   * @returns Validation response
   */
  async validateAddress(address: ShippingAddress): Promise<AddressValidationResponse> {
    try {
      const response = await api.post<AddressValidationResponse>('/tax/validate-address', address);
      return response.data;
    } catch (error) {
      console.error('Address validation error:', error);
      // Return a default response that allows checkout
      return {
        valid: true,
        validation_status: 'error',
        original_address: address,
        suggested_address: null,
        messages: ['Address validation service unavailable - proceeding with entered address']
      };
    }
  }
}

export default new TaxService();